import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { formatWeight, formatWeightDate } from '../utils/weightControl';
import './WeightProgressModal.css';

// График динамики веса участника по соревновательным заездам.
// Открывается по нажатию на значок контроля веса (на мобильных подсказки нет).
// points: [{ date, weight, eventName, routeName, baseline }] — см. getRiderWeightSeries
const WeightProgressModal = ({ isOpen, onClose, riderName, points }) => {
  const { t, language } = useLanguage();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768
  );

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  if (!isOpen) return null;

  const data = (points || []).filter(p => p.weight !== null && p.weight !== undefined);

  // Геометрия графика — как в графике мест по этапам: на мобильных система координат
  // близка к ширине экрана, поэтому подписи не мельчают при масштабировании SVG
  const width = isMobile ? 380 : 640;
  const height = isMobile ? 300 : 320;
  const margin = isMobile
    ? { top: 20, right: 16, bottom: 42, left: 44 }
    : { top: 24, right: 28, bottom: 44, left: 58 };
  const pointRadius = isMobile ? 7 : 8;
  const hitRadius = isMobile ? 16 : 18;
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;

  const weights = data.map(p => p.weight);
  const minWeight = weights.length ? Math.min(...weights) : 0;
  const maxWeight = weights.length ? Math.max(...weights) : 0;

  // Домен расширен на полкило в обе стороны, чтобы точки не липли к краям.
  // При одинаковом весе получаем ровный коридор вокруг значения
  const yMin = Math.floor((minWeight - 0.5) * 2) / 2;
  const yMax = Math.ceil((maxWeight + 0.5) * 2) / 2;

  const xFor = (index) => {
    if (data.length === 1) return margin.left + plotWidth / 2;
    return margin.left + (plotWidth * index) / (data.length - 1);
  };

  // Ось Y обычная: больше вес — выше точка
  const yFor = (weight) => {
    if (yMax === yMin) return margin.top + plotHeight / 2;
    return margin.top + plotHeight - (plotHeight * (weight - yMin)) / (yMax - yMin);
  };

  // Деления оси Y кратно 0.5 кг, не больше шести
  const buildYTicks = () => {
    const span = yMax - yMin;
    const step = Math.max(0.5, Math.ceil((span / 5) * 2) / 2);
    const ticks = [];
    for (let value = yMin; value < yMax; value += step) {
      ticks.push(Math.round(value * 10) / 10);
    }
    ticks.push(Math.round(yMax * 10) / 10);
    return ticks;
  };

  const yTicks = buildYTicks();

  // Линия без сглаживания: между заездами промежуточных значений нет
  const linePath = data
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(p.weight)}`)
    .join(' ');

  // Граница между заездами до турнира и заездами турнира
  const firstEventIndex = data.findIndex(p => !p.baseline);
  const startDividerX = firstEventIndex > 0
    ? (xFor(firstEventIndex - 1) + xFor(firstEventIndex)) / 2
    : null;

  // Подписи всех дат по оси X не помещаются — прореживаем
  const labelStep = Math.max(1, Math.ceil(data.length / (isMobile ? 4 : 7)));

  const hovered = hoveredIndex !== null ? data[hoveredIndex] : null;

  // Разбиваем длинное название гонки на строки по ширине подсказки
  const wrapText = (text, maxChars, maxLines) => {
    const words = String(text).split(/\s+/).filter(Boolean);
    const lines = [];
    let current = '';

    words.forEach(word => {
      const candidate = current ? `${current} ${word}` : word;

      if (candidate.length <= maxChars) {
        current = candidate;
        return;
      }

      if (current) lines.push(current);
      current = word.length > maxChars ? `${word.slice(0, maxChars - 1)}…` : word;
    });

    if (current) lines.push(current);

    if (lines.length > maxLines) {
      const kept = lines.slice(0, maxLines);
      kept[maxLines - 1] = `${kept[maxLines - 1].slice(0, Math.max(1, maxChars - 1))}…`;
      return kept;
    }

    return lines;
  };

  const renderTooltip = () => {
    if (!hovered) return null;

    const fontSize = isMobile ? 13 : 12;
    const charWidth = fontSize * 0.62;
    const paddingX = 10;
    const lineHeight = fontSize + 4;

    const maxBoxWidth = Math.min(isMobile ? 300 : 320, width - 24);
    const maxChars = Math.max(10, Math.floor((maxBoxWidth - paddingX * 2) / charWidth));

    const titleLine = t('results.weight_chart_point')
      .replace('{date}', formatWeightDate(hovered.date, language))
      .replace('{weight}', formatWeight(hovered.weight));
    const subText = hovered.eventName || hovered.routeName || '';
    const subLines = subText ? wrapText(subText, maxChars, 2) : [];
    const allLines = [titleLine, ...subLines];

    const longest = allLines.reduce((max, line) => Math.max(max, line.length), 0);
    const boxWidth = Math.min(maxBoxWidth, Math.max(110, longest * charWidth + paddingX * 2));
    const boxHeight = 12 + allLines.length * lineHeight;

    const x = xFor(hoveredIndex);
    const y = yFor(hovered.weight);

    // Прижимаем подсказку к области графика, чтобы она не выходила за край
    const boxX = Math.min(
      Math.max(x - boxWidth / 2, Math.min(margin.left, 12)),
      width - Math.min(margin.right, 12) - boxWidth
    );
    const boxY = y - boxHeight - 14 < margin.top ? y + 14 : y - boxHeight - 14;

    return (
      <g className="weight-progress-tooltip" pointerEvents="none">
        <rect x={boxX} y={boxY} width={boxWidth} height={boxHeight} rx="6" />
        {allLines.map((line, index) => (
          <text
            key={`tooltip-line-${index}`}
            x={boxX + paddingX}
            y={boxY + 6 + lineHeight * index + fontSize}
            className={index === 0 ? '' : 'weight-progress-tooltip-sub'}
          >
            {line}
          </text>
        ))}
      </g>
    );
  };

  return (
    // График открывается в том числе поверх модалки результатов —
    // клик по подложке не должен закрывать ее вместе с графиком
    <div
      className="weight-progress-overlay"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className="weight-progress-modal" onClick={(e) => e.stopPropagation()}>
        <div className="weight-progress-header">
          <div>
            <h3 className="weight-progress-title">{t('results.weight_chart_title')}</h3>
            <div className="weight-progress-subtitle">{riderName}</div>
          </div>
          <button className="weight-progress-close" onClick={onClose} aria-label={t('common.close')}>
            &times;
          </button>
        </div>

        <div className="weight-progress-body">
          {data.length === 0 ? (
            <div className="weight-progress-empty">{t('results.weight_chart_no_data')}</div>
          ) : (
            <>
              <svg
                className="weight-progress-chart"
                viewBox={`0 0 ${width} ${height}`}
                role="img"
                aria-label={`${t('results.weight_chart_title')}: ${riderName}`}
              >
                {/* Сетка и подписи оси Y (вес) */}
                {yTicks.map(tick => (
                  <g key={`y-${tick}`}>
                    <line
                      className="weight-progress-grid"
                      x1={margin.left}
                      x2={width - margin.right}
                      y1={yFor(tick)}
                      y2={yFor(tick)}
                    />
                    <text
                      className="weight-progress-axis-label"
                      x={margin.left - 10}
                      y={yFor(tick) + 4}
                      textAnchor="end"
                    >
                      {formatWeight(tick)}
                    </text>
                  </g>
                ))}

                {/* Старт турнира: слева от линии — заезды, с которыми участник в него зашел */}
                {startDividerX !== null && (
                  <g>
                    <line
                      className="weight-progress-start-line"
                      x1={startDividerX}
                      x2={startDividerX}
                      y1={margin.top}
                      y2={margin.top + plotHeight}
                    />
                    <text
                      className="weight-progress-start-label"
                      x={startDividerX + 4}
                      y={margin.top + 12}
                    >
                      {t('results.weight_chart_event_start')}
                    </text>
                  </g>
                )}

                {/* Подписи оси X (даты заездов) */}
                {data.map((p, i) => (
                  (i % labelStep === 0 || i === data.length - 1) && (
                    <text
                      key={`x-${p.date}-${i}`}
                      className="weight-progress-axis-label"
                      x={xFor(i)}
                      y={height - margin.bottom + 20}
                      textAnchor="middle"
                    >
                      {formatWeightDate(p.date, language).slice(0, 5)}
                    </text>
                  )
                ))}

                <text
                  className="weight-progress-axis-title"
                  x={margin.left + plotWidth / 2}
                  y={height - 8}
                  textAnchor="middle"
                >
                  {t('results.weight_chart_axis_rides')}
                </text>
                <text
                  className="weight-progress-axis-title"
                  x={-(margin.top + plotHeight / 2)}
                  y={14}
                  transform="rotate(-90)"
                  textAnchor="middle"
                >
                  {t('results.weight_chart_axis_weight')}
                </text>

                {/* Линия веса */}
                <path className="weight-progress-line" d={linePath} />

                {data.map((p, i) => (
                  <g key={`point-${p.date}-${i}`}>
                    <circle
                      className={`weight-progress-point ${p.baseline ? 'weight-progress-point-baseline' : ''}`}
                      cx={xFor(i)}
                      cy={yFor(p.weight)}
                      r={pointRadius}
                    />
                    {/* Увеличенная область нажатия */}
                    <circle
                      className="weight-progress-hit"
                      cx={xFor(i)}
                      cy={yFor(p.weight)}
                      r={hitRadius}
                      onMouseEnter={() => setHoveredIndex(i)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      onClick={() => setHoveredIndex(i)}
                    >
                      <title>
                        {t('results.weight_chart_point')
                          .replace('{date}', formatWeightDate(p.date, language))
                          .replace('{weight}', formatWeight(p.weight))}
                      </title>
                    </circle>
                  </g>
                ))}

                {/* Подписываем только первую и последнюю точки */}
                {[0, data.length - 1].filter((v, i, arr) => arr.indexOf(v) === i).map(index => (
                  <text
                    key={`label-${index}`}
                    className="weight-progress-point-label"
                    x={xFor(index)}
                    y={yFor(data[index].weight) - pointRadius - 7}
                    textAnchor="middle"
                  >
                    {formatWeight(data[index].weight)}
                  </text>
                ))}

                {renderTooltip()}
              </svg>

              <div className="weight-progress-caption">{t('results.weight_chart_caption')}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeightProgressModal;
